import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { CoreTabs, ICoreTab } from '@components-clean/core/core-tabs';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import DataViews from '@pages-clean/admin/study/admin-data-views/data-views';
import Filters from '@pages-clean/admin/study/admin-data-views/filters';
import { DataFilterResultType, ExtraDataFields } from '@pages-clean/admin/study/admin-data-views/filters/types';
import { IUser } from '@contexts/user-context';
import { useTranslation } from '@hooks/index';
import {
    GET_DATA_REVIEW_TABLES,
    GET_DATA_VIEW_DATA_FILTERS,
    GET_DATA_VIEW_SITE_AND_SUBJECT_FILTERS,
    GET_EXTRA_FIELDS,
    GET_PANELS,
} from '@queries/central-monitoring';
import {
    getPanels,
    getPanelsVariables,
    dataReviewTables,
    dataViewDataFilters,
    dataViewDataFilters_OpraQueries_dataViewDataFilters_DataViewFilter,
    dataViewDataFiltersVariables,
    dataViewSiteAndSubjectFilters_OpraQueries_dataViewSiteAndSubjectFilters_DataViewFilter as DataViewFilter,
    dataViewSiteAndSubjectFilters,
    dataViewSiteAndSubjectFiltersVariables,
} from '@queries/generated/central-monitoring.generated';
import { StudyFields } from '@queries/generated/fragments.generated';

const AdminDataViews = ({ user, study }: { user: IUser; study: StudyFields }) => {
    const [dataFilters, setDataFilters] = useState<DataFilterResultType[]>([]);
    const [siteSubjectFilters, setSiteSubjectFilters] = useState<DataFilterResultType[]>([]);
    const { confirmDiscardChanges, anyComponentHasChanges, discardChanges } = useUnsavedChangesContext();

    const [activeTab, setActiveTab] = useState<number>(0);

    const studyRef = study?.id as string;

    const { data: panelsData, loading: panelsLoading } = useQuery<getPanels, getPanelsVariables>(GET_PANELS, {
        skip: !user || !study,
        variables: {
            organisationId: user.organisationId,
            studyId: studyRef,
        },
    });
    const { data: dataTablesData, loading: dataTablesLoading } = useQuery<dataReviewTables>(GET_DATA_REVIEW_TABLES, {
        skip: !panelsData || !user || !study,
        variables: {
            studyId: studyRef,
        },
    });
    const { data: filtersData, loading: filtersLoading } = useQuery<dataViewDataFilters, dataViewDataFiltersVariables>(
        GET_DATA_VIEW_DATA_FILTERS,
        {
            skip: !dataTablesData || !user || !study,
            variables: { studyId: studyRef },
        },
    );

    useEffect(() => {
        setDataFilters(
            (filtersData?.dataViewDataFilters as dataViewDataFilters_OpraQueries_dataViewDataFilters_DataViewFilter[]) ??
                [],
        );
    }, [filtersData]);

    const { data: siteAndSubjectFilters, loading: siteAndSubjectFiltersLoading } = useQuery<
        dataViewSiteAndSubjectFilters,
        dataViewSiteAndSubjectFiltersVariables
    >(GET_DATA_VIEW_SITE_AND_SUBJECT_FILTERS, {
        skip: !dataTablesData || !user || !study,
        variables: { studyId: studyRef },
    });

    useEffect(() => {
        setSiteSubjectFilters((siteAndSubjectFilters?.dataViewSiteAndSubjectFilters as DataViewFilter[]) ?? []);
    }, [siteAndSubjectFilters]);

    const { data: extraFields, loading: fieldsLoading } = useQuery<ExtraDataFields>(GET_EXTRA_FIELDS, {
        skip: !siteAndSubjectFilters || !user || !study,
        variables: {
            studyId: studyRef,
        },
    });

    useEffect(() => {
        setActiveTab(0);
    }, [study]);

    const isLoading =
        !panelsData ||
        panelsLoading ||
        !dataTablesData ||
        dataTablesLoading ||
        !filtersData ||
        filtersLoading ||
        siteAndSubjectFiltersLoading;

    const { t } = useTranslation();
    const tabsData: ICoreTab[] = [
        {
            tabTitle: t('admin:dataViews'),
            tabContent: (
                <DataViews
                    study={study}
                    user={user}
                    isLoading={isLoading}
                    panelsData={panelsData}
                    dataTablesData={dataTablesData}
                    filtersData={dataFilters}
                />
            ),
            dataTestId: 'data-views-tab',
        },
        {
            tabTitle: t('admin:filters.filters'),
            tabContent: (
                <Filters
                    user={user}
                    study={study}
                    filtersData={dataFilters}
                    siteAndSubjectFilters={siteSubjectFilters}
                    isLoading={filtersLoading || siteAndSubjectFiltersLoading || fieldsLoading}
                    onDataFilterChange={(val) => setDataFilters(val)}
                    onSiteSubjectFilterChange={(val) => setSiteSubjectFilters(val)}
                    extraDataFields={extraFields}
                />
            ),
            dataTestId: 'filters-tab',
        },
    ];

    const updateActiveTab = async (tabIndex: number) => {
        if (anyComponentHasChanges()) {
            const confirmed = await confirmDiscardChanges('admin-data-view-unsaved-changes-dialog');
            if (!confirmed) {
                return;
            }
            discardChanges();
        }
        setActiveTab(tabIndex);
    };

    return (
        <div className="k-mt-2.5">
            <CoreTabs
                tabsData={tabsData}
                bodyPadding="none"
                data-testid="data-view-config-tabs"
                type="page"
                activeTab={activeTab}
                setActiveTab={updateActiveTab}
            />
        </div>
    );
};
export default AdminDataViews;
